import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/AdminShell";
import { toast } from "sonner";
import { ArrowLeft, QrCode, Activity, BookOpen } from "lucide-react";
import { SignOnSetup } from "@/components/SignOnSetup";
import { SignOnLive } from "@/components/SignOnLive";

const STEPS = [
  ["Turn sign-on on", "The QR and reminder links work for draft and published events once sign-on is switched on."],
  ["Switch sign-on on", "Turn on 'Sign-on open' in Setup and press Save."],
  ["Build the form", "First name, Last name and Mobile are always asked. Add any extra questions (e.g. licence number, radio callsign) and choose if they're required."],
  ["Set the message & disclaimer", "Edit the acknowledgement message. Optionally upload a disclaimer PDF — volunteers can open it if they wish before ticking the box."],
  ["Choose reminder timings", "Pick how long after each event element starts the reminder SMS goes to people not signed on, and when the status report goes to the Stage Commander and Deputy (up to 60 minutes)."],
  ["Print the QR", "Download the QR (.png) and put it at every sign-on point. One QR covers the whole event."],
  ["Test it", "Tick 'Allow sign-on before the event day', scan the QR on your phone and sign on as yourself. Untick it afterwards."],
  ["On the day", "Open the Live tab. A volunteer counts as signed on once at least two of First name, Last name and Mobile match their roster entry. Export sheets any time."],
];

function Instructions() {
  return (
    <aside className="border border-[#002FA7]/20 bg-[#002FA7]/[0.03] p-5" data-testid="signon-instructions">
      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#002FA7]"><BookOpen className="h-4 w-4" /> How to set up sign-on</div>
      <ol className="mt-3 space-y-3">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="flex gap-3 text-sm">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-[#002FA7] font-mono text-xs font-bold text-white">{i + 1}</span>
            <span><b>{t}</b><span className="block text-xs text-zinc-600">{d}</span></span>
          </li>
        ))}
      </ol>
    </aside>
  );
}

export default function AdminSignOn() {
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "live" ? "live" : "setup";
  const [cfg, setCfg] = useState(null);
  const [eventName, setEventName] = useState("");
  const [evStatus, setEvStatus] = useState("");

  useEffect(() => {
    api.get(`/admin/events/${eventId}/signon`).then(({ data }) => setCfg(data)).catch(() => toast.error("Could not load sign-on settings"));
    api.get(`/admin/events/${eventId}`).then(({ data }) => { setEventName(data.name); setEvStatus(data.status); }).catch(() => {});
  }, [eventId]);

  const tabBtn = (key, label, Icon) => (
    <button onClick={() => setParams({ tab: key })} data-testid={`signon-tab-${key}`} className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-semibold ${tab === key ? "border-[#FF3B30] text-black" : "border-transparent text-zinc-500 hover:text-black"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <AdminShell event={{ id: eventId, name: eventName, status: evStatus, slug: cfg?.slug }} active="signon" title="Sign-on">
      <div className="z-20 lg:sticky lg:top-14 border-b border-black/10 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-end gap-4 px-6 pt-3">
          <h1 className="pb-3 font-display text-2xl font-black tracking-tighter">On-the-day sign-on</h1>
          <nav className="ml-auto flex">{tabBtn("setup", "Setup", QrCode)}{tabBtn("live", "Live sign-on", Activity)}</nav>
        </div>
      </div>
      <main className="mx-auto max-w-6xl px-6 pb-28 pt-8">
        {!cfg ? <div className="text-sm text-zinc-500">Loading…</div> : tab === "live" ? (
          <SignOnLive eventId={eventId} cfg={cfg} onCfgChange={setCfg} />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <SignOnSetup eventId={eventId} cfg={cfg} setCfg={setCfg} />
            <div className="lg:sticky lg:top-6 lg:self-start"><Instructions /></div>
          </div>
        )}
      </main>
    </AdminShell>
  );
}
